import { type MouseEvent, useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import IconClose from '@/assets/icons/icon-close.svg'
import { IconLoading } from '@/assets/icons/icon-loading'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/collapse.tsx'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $account } from '@/models/account.ts'
import { openDialog } from '@/models/dialogs.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { noop } from '@/utils/misc'

import { type SavedAccount } from './types'
import { useMultipleAccounts } from './useMultipleAccounts'

const MAX_ACCOUNT_COUNT = 15

export const AccountAccordion = () => {
  const account = useUnit($account)
  const [isOpen, setOpen] = useState(false)

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev)
  }, [])

  const {
    accounts,
    openLoginDialog,
    loadingUserId,
    onUserLogin,
    onUserLogout,
  } = useMultipleAccounts()

  const handleUserClick = useCallback(
    (user: SavedAccount) => {
      if (user.id === account?.userId) {
        handleToggle()

        return
      }
      onUserLogin(user)
    },
    [onUserLogin, account, handleToggle],
  )

  const handleAddAccount = useCallback(() => {
    if (accounts.length >= MAX_ACCOUNT_COUNT) {
      openDialog({
        type: DIALOG_TYPE.CONFIRMATION,
        title: "You can't add more accounts",
        body: 'Please remove one of the existing accounts to add a new one.',
        confirmText: 'OK',
        hideCancel: true,
      })
    } else {
      openLoginDialog()
    }
  }, [accounts, openLoginDialog])

  const handleRemoveAccount = useCallback(
    async (event: MouseEvent<HTMLOrSVGElement>, userId: number) => {
      event.stopPropagation()
      openDialog({
        type: DIALOG_TYPE.CONFIRMATION,
        title: 'Remove account',
        body: 'Are you sure you want to remove this account?',
        confirmText: 'Remove',
        confirmButtonVariant: 'destructive',
        onConfirm: async () => {
          onUserLogout(userId)
        },
      })
    },
    [onUserLogout],
  )

  if (!account) {
    return null
  }

  return (
    <Accordion
      collapsible
      type="single"
      onChange={handleToggle}
      value={isOpen ? 'open' : 'closed'}
    >
      <AccordionItem
        value="open"
        className="rounded-lg border-[1px] bg-secondary-50 dark:border-[#626678] dark:bg-secondary-600"
      >
        <AccordionTrigger
          className="p-3"
          onClick={handleToggle}
          data-testid="user-logged-in-section"
        >
          <div className="flex flex-col items-start space-y-1">
            <p
              className="text-md truncate font-medium leading-none text-text-default"
              data-testid="user-logged-in-name"
            >
              {account.name}
            </p>
            <p
              className="truncate text-xs leading-none text-text-sub"
              data-testid="user-logged-in-email"
            >
              {account.email}
            </p>
          </div>
        </AccordionTrigger>
        <AccordionContent childClassName="py-3 px-1 pt-0 max-h-[190px] overflow-y-auto">
          <RadioGroup
            value={account.email}
            onChange={noop}
            data-testid="logged-in-users-list"
          >
            {accounts.map((user) => (
              <div
                key={user.id}
                className="group flex cursor-pointer items-center justify-between rounded-lg px-2 hover:bg-primary-100 dark:hover:bg-secondary-500"
                onClick={() => handleUserClick(user)}
                role="button"
                tabIndex={0}
                data-testId="logged-in-user"
              >
                <div className="flex items-center gap-2 py-2">
                  <img
                    src={user.profilePicture}
                    alt="profile picture"
                    className="nsfw-ignore h-8 w-8 rounded-full bg-border"
                  />
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-text-default">
                      {user.name}
                    </p>
                    <p className="text-xs text-text-sub">{user.email}</p>
                  </div>
                </div>
                {loadingUserId === user.id ? (
                  <IconLoading className="mr-0 h-4 w-4 animate-spin" />
                ) : (
                  <div className="relative">
                    <Radio
                      label=""
                      value={user.email}
                      className="space-x-0"
                    />
                    {account.userId !== user.id && (
                      <IconButton
                        onClick={(event) => handleRemoveAccount(event, user.id)}
                        icon={IconClose}
                        iconClassName="h-3 w-3 text-white"
                        className="absolute left-0 top-0 mr-0 h-4 w-4 rounded-full bg-secondary-200 opacity-0 hover:bg-danger-default group-hover:opacity-100 dark:bg-secondary-400 dark:hover:bg-danger-default"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </RadioGroup>
          <Button
            variant="ghost"
            className="text-primary -mb-2 h-auto w-full justify-start gap-2 px-2 py-2 hover:bg-primary-100 dark:hover:bg-secondary-500"
            onClick={handleAddAccount}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-[#272A39]">
              <PlusIcon className="text-secondary-300" />
            </div>
            Add Account
          </Button>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
